import { EventPageFragment } from '@graphql/generated';
import { useHistory } from '@shared/core';
import { windowExists } from '@shared/utils/windowExists';
import { useCallback, useEffect } from 'react';
import { empty, fromEvent } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { useScrollTo } from '../providers/ScrollToProvider/ScrollTo.context';

const watchScroll = (scrollNode?: React.RefObject<HTMLDivElement>) => {
  if (windowExists()) {
    return fromEvent(scrollNode?.current || window.document, 'scroll', { capture: true }).pipe(auditTime(5));
  } else {
    return empty();
  }
};

const scrollDecider = (() => {
  let timeout: undefined | ReturnType<typeof setTimeout>;
  return (eventType: 'scroll' | 'location', callback?: () => void) => {
    if (eventType === 'scroll') {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        timeout = undefined;
      }, 300);
    } else if (!timeout) {
      callback && callback();
    }
  };
})();

const slugFromPathname = (pathname: string): string | undefined => pathname.split('/')[2];

export const useLocationScroller = (pages: Array<EventPageFragment>, isMounted: boolean) => {
  const history = useHistory();
  const { scrollTo } = useScrollTo();

  const scrollToPathname = useCallback(
    (pathname: string) => {
      const page = pages.find(p => slugFromPathname(pathname) && p.pageSlug.toLowerCase() === slugFromPathname(pathname)?.toLowerCase());
      if (page) {
        scrollTo(page.id);
      }
    },
    [pages, scrollTo]
  );

  useEffect(() => {
    const scrolling = watchScroll();
    scrolling.subscribe(() => {
      scrollDecider('scroll');
    });

    const unlisten = history.listen(location => {
      scrollDecider('location', () => {
        scrollToPathname(location.pathname);
      });
    });
    return () => {
      unlisten();
    };
  }, [history, pages, scrollTo, scrollToPathname]);

  useEffect(() => {
    if (isMounted) {
      scrollToPathname(history.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);
};
